<template>
  <div class="bg-[#1A1A1A] rounded-xl p-5 shadow-md mb-4">
    <div class="flex justify-between items-center mb-2">
      <h3 class="text-lg font-semibold text-[#C59C2F]">
        {{ order.title }}
      </h3>
      <span
        :class="statusClass"
        class="text-xs font-semibold px-3 py-1 rounded-full capitalize"
      >
        {{ RequestStatus[order.status] }}
      </span>
    </div>

    <div class="text-sm text-gray-700 dark:text-gray-300 mb-1">
      <strong>Price:</strong> £{{ order.payment?.amount?.toFixed(2) }}
    </div>
    <div v-if="loadingUser" class="text-sm text-gray-500 italic">
      Loading user info...
    </div>
    <div v-else-if="user" class="text-sm text-gray-700 dark:text-gray-300 mb-1">
      <strong>User:</strong> {{ user.fullName }}
      <span class="text-xs text-gray-400">({{ user.email }})</span>
    </div>
    <div class="text-sm text-gray-700 dark:text-gray-300 mb-2">
      <strong>Created:</strong>
      {{ formatFirebaseTimestamp(order.createdAt, true) }}
    </div>

    <details class="mt-2">
      <summary class="text-[#C59C2F] cursor-pointer text-sm mb-1">
        View Details
      </summary>
      <div
        class="mt-3 border border-[#333] rounded-lg p-4 bg-black/30 text-white space-y-2 text-sm"
      >
        <div
          v-for="(value, key) in order.inputData"
          :key="key"
          class="flex justify-between gap-4"
        >
          <span class="opacity-70">{{ formatKey(key) }}</span>
          <span class="text-right font-medium text-white">
            {{ Array.isArray(value) ? value.join(", ") : value }}
          </span>
        </div>
      </div>
    </details>
  </div>
</template>

<script setup lang="ts">
import { db } from '@/config/firebase';
import { formatFirebaseTimestamp } from '@/helpers';
import { Order, RequestStatus } from '@/types/order';
import { doc, getDoc } from 'firebase/firestore';

import {
  computed, defineProps, onMounted, ref,
} from 'vue';

const props = defineProps<{ order: Order }>();

const user = ref<{ fullName: string; email: string } | null>(null);
const loadingUser = ref(true);

onMounted(async () => {
  try {
    const docRef = doc(db, 'users', props.order.userId);
    const snap = await getDoc(docRef);
    if (snap.exists()) {
      user.value = snap.data() as { fullName: string; email: string };
    }
  } catch (e) {
    console.error('Failed to fetch user info:', e);
  } finally {
    loadingUser.value = false;
  }
});

function formatKey(key: string) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (str) => str.toUpperCase());
}

const statusClass = computed(() => {
  switch (props.order.status) {
    case RequestStatus.Processing:
      return 'bg-yellow-100 text-yellow-800';
    case RequestStatus['In Progress']:
      return 'bg-blue-100 text-blue-800';
    case RequestStatus.Completed:
      return 'bg-green-100 text-green-800';
    case RequestStatus.Cancelled:
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-200 text-gray-800';
  }
});
</script>
